import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { UserPlus, Loader2, Mail, Lock, User, Phone, MapPin, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    date_of_birth: '',
    address: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/student/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth || null,
          address: formData.address || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Registration failed');
        setLoading(false);
        return;
      }

      alert('Registration successful! Please login to continue.');
      navigate('/student/login');
    } catch (error) {
      console.error('Registration error:', error);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      <Header />

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="text-center bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
              <div className="flex justify-center mb-4">
                <UserPlus size={48} />
              </div>
              <CardTitle className="text-3xl">Student Registration</CardTitle>
              <CardDescription className="text-red-100 text-base">
                Join Vidya365 to access courses, tests, and resources
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="inline mr-2" size={16} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="inline mr-2" size={16} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline mr-2" size={16} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Lock className="inline mr-2" size={16} />
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                      placeholder="Minimum 6 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Lock className="inline mr-2" size={16} />
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                      placeholder="Re-enter password"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline mr-2" size={16} />
                    Date of Birth (Optional)
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline mr-2" size={16} />
                    Address (Optional)
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="Your address"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Registering...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2" size={20} />
                      Register
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <Link to="/student/login" className="text-red-600 hover:underline font-medium">
                    Login here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Location Card */}
          <Card className="shadow-xl mt-8">
            <CardHeader className="bg-gradient-to-br from-red-50 to-white border-b-2 border-red-100">
              <CardTitle className="flex items-center text-2xl text-gray-900">
                <MapPin className="mr-2 text-red-600" size={24} />
                Visit Our Coaching Center
              </CardTitle>
              <CardDescription>
                Come visit us for a free demo class and campus tour
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📍 Address</h4>
                  <p className="text-gray-700">
                    Vidya365 Coaching Center<br />
                    Unit 8, Apsara Arcade<br />
                    Near Karol Bagh Metro Gate 7<br />
                    Pusa Road, Karol Bagh<br />
                    New Delhi - 110005
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📞 Contact</h4>
                  <p className="text-gray-700">
                    Phone: <a href="tel:+918750410365" className="text-red-600 hover:underline">+91 87504 10365</a><br />
                    Email: <a href="mailto:admin@vidya365.com" className="text-red-600 hover:underline">admin@vidya365.com</a>
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🕒 Office Hours</h4>
                  <p className="text-gray-700">
                    Monday - Saturday: 8:00 AM - 8:00 PM<br />
                    Sunday: 9:00 AM - 5:00 PM
                  </p>
                </div>

                {/* Google Maps Embed */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">🗺️ Find Us on Map</h4>
                  <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.491206146657!2d77.19421679999999!3d28.645007399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd81d2c99379%3A0xc5e1f19124cadee2!2sVIDYA365%20Coaching%20%7C%20SSC%20CGL%2C%20CHSL%2C%20MTS%2C%20GD%20%7C%20RBI%20Grade%20B%2C%20NABARD%2C%20SEBI%2C%20SBI%20PO%2C%20IBPS!5e0!3m2!1sen!2sin!4v1775306770634!5m2!1sen!2sin" 
                      width="100%" 
                      height="350" 
                      style={{border: 0}} 
                      allowFullScreen="" 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Vidya365 Coaching Location"
                    ></iframe>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Call us before your visit to schedule a personalized counseling session and campus tour!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StudentRegister;
